import React, { useCallback, useContext, useState } from 'react';
import { Button, Modal, Select } from 'antd';
import dayjs from 'dayjs';

import PackageContext from '@totem/components/software/software_package/packageContext';
import { VendorAutoComplete } from '@totem/components/software/software_package/types';
import ModalFooter from '@totem/components/ui_components/dialogs/ModalFooter';
import ModalTitle from '@totem/components/ui_components/dialogs/ModalTitle';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { ForeignKey } from '@totem/types/common';
import { isNotNull, isNull } from '@totem/utilities/common';
import { debounce } from '@totem/utilities/debounce';
import {
  AUTO_COMPLETE_ENDPOINT,
  DEVICES_ENDPOINT,
} from '@totem/utilities/endpoints';
import { CheckResponseShowError } from '@totem/utilities/responseUtilities';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const styles = {
  dialog: {
    maxHeight: '80%',
    maxWidth: '850px',
  },
  label: {
    fontSize: '12px',
    fontWeight: 600,
  },
  select: {
    width: '100%',
  },
};

const DEBOUNCE_TIME = 1500;

const OpenCveKeyModal = () => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const { data, action, onAction } = useContext(PackageContext);
  const [isSending, setIsSending] = useState<boolean>(false);
  const [vendorSearch, setVendorSearch] = useState<string>('');
  const [vendorOptions, setVendorOptions] = useState<VendorAutoComplete[]>([]);
  const [selectedVendor, setSelectedVendor] = useState<VendorAutoComplete>();
  const [vendorKey, setVendorKey] = useState<string>('');
  const [productSearch, setProductSearch] = useState<string>('');
  const [productOptions, setProductOptions] = useState<string[]>([]);
  const [productKey, setProductKey] = useState<string>('');
  const [isSendingVendorKey, setIsSendingVendorKey] = useState<boolean>(false);
  const [isSendingProductKey, setIsSendingProductKey] =
    useState<boolean>(false);

  const sendCreateVendorKey = useCallback(
    async (payload: any) => {
      if (isSendingVendorKey) {
        return;
      }
      setIsSendingVendorKey(true);

      fetch(`${DEVICES_ENDPOINT}/software/catalog/${data.package.id}/keys`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      }).then((res) => {
        CheckResponseShowError(res);

        setIsSendingVendorKey(false);

        onAction('refresh');
      });
    },
    [getApiToken],
  );

  const sendCreateProductKey = useCallback(
    async (payload: any) => {
      if (isSendingProductKey) {
        return;
      }
      setIsSendingProductKey(true);

      fetch(`${DEVICES_ENDPOINT}/software/catalog/${data.package.id}/keys`, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(payload),
      }).then((res) => {
        CheckResponseShowError(res);

        setIsSendingProductKey(false);

        onAction('refresh');
      });
    },
    [getApiToken],
  );

  const getVendorOptions = useCallback(
    async (searchTerm: string) => {
      if (isSending) {
        return;
      }
      setIsSending(true);

      const uri = `${AUTO_COMPLETE_ENDPOINT}/openCveVendors`;

      const request = {
        type: 'openCveVendors',
        searchTerms: searchTerm,
        limit: 15,
        filters: [],
      };

      fetch(uri, {
        method: 'POST',
        headers: new Headers({
          Authorization: `Bearer ${getApiToken()}`,
        }),
        body: JSON.stringify(request),
      })
        .then((res) => res.json())
        .then((result: VendorAutoComplete[]) => {
          if (result !== null) {
            setVendorOptions(result);
          } else {
            setVendorOptions([]);
          }
        })
        .then(() => {
          setIsSending(false);
        });
    },
    [getApiToken],
  );

  const handleVendorSelected = (vendorName: string) => {
    setVendorKey(vendorName);

    setVendorSearch(vendorName);
    const newSelectedVendor = vendorOptions.find(
      (chk) => chk.vendor === vendorName,
    );
    if (isNotNull(newSelectedVendor)) {
      setSelectedVendor(newSelectedVendor);
    }
  };

  const getProductOptions = (localProductSearch: string) => {
    if (isNull(selectedVendor)) {
      return [];
    }
    const filteredProducts = selectedVendor.products
      .filter((chk) => chk.indexOf(localProductSearch) > -1)
      .sort((compA, compB) => sortStringAscending(compA, compB));
    setProductOptions(filteredProducts);
    return filteredProducts;
  };

  const handleSubmit = () => {
    if (vendorKey !== '' && productKey !== '') {
      const payloadVendor: ForeignKey = {
        system: 'OpenCVE',
        instance: 'vendor',
        key: vendorKey,
        uid: '',
        createdByType: 'user',
        createdByName: userProfile.email,
        createdById: userProfile.id,
        createdAt: dayjs().toISOString(),
      };

      const payloadProduct: ForeignKey = {
        system: 'OpenCVE',
        instance: 'product',
        key: productKey,
        uid: '',
        createdByType: 'user',
        createdByName: userProfile.email,
        createdById: userProfile.id,
        createdAt: dayjs().toISOString(),
      };

      sendCreateVendorKey(payloadVendor);

      sendCreateProductKey(payloadProduct);
    }
  };

  return (
    <Modal
      open={action === 'key_add'}
      onCancel={() => onAction('')}
      title={<ModalTitle>Add Foreign Key</ModalTitle>}
      confirmLoading={false}
      style={styles.dialog}
      footer={
        <ModalFooter>
          <Button onClick={() => onAction('')}>Close</Button>
          <Button onClick={handleSubmit} type="primary">
            Submit
          </Button>
        </ModalFooter>
      }
    >
      <div style={styles.label}>Vendor:</div>
      <Select
        style={styles.select}
        showSearch
        value={vendorSearch}
        onSearch={(selectedSystemName: string) => {
          setVendorSearch(selectedSystemName);

          debounce(getVendorOptions(selectedSystemName), DEBOUNCE_TIME);
        }}
        onSelect={(newVendor) => {
          handleVendorSelected(newVendor);
        }}
        options={
          isNull(vendorOptions)
            ? []
            : vendorOptions.map((option) => {
                return { value: option.vendor, label: option.vendor };
              })
        }
      />

      <div style={styles.label}>Product:</div>
      <Select
        style={styles.select}
        showSearch
        value={productSearch}
        onSearch={(selectedProductName: string) => {
          setProductSearch(selectedProductName);

          debounce(getProductOptions(selectedProductName), DEBOUNCE_TIME);
        }}
        onSelect={(newProduct) => {
          setProductKey(newProduct);

          setProductSearch(newProduct);
        }}
        options={
          isNull(productOptions)
            ? []
            : productOptions.map((option) => {
                return { value: option, label: option };
              })
        }
      />
    </Modal>
  );
};

export default OpenCveKeyModal;
